// src/layouts/profile.js
import { useMemo, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";
import PlatformSettings from "layouts/profile/components/PlatformSettings";
import Header from "layouts/profile/components/Header";

import { useAuth } from "layouts/auth/AuthContext";
import { updateUser } from "layouts/autenticacion/usuarios/services/users";
import { apiPermsToPlatform, platformToApiPerms } from "utils/permMaps";

import api from "services/api";

const bool = (v) => v === true || v === 1 || v === "1";

const normalizeUser = (raw = {}) => {
  const u = raw.user ?? raw;
  const username = u.username || (u.email ? u.email.split("@")[0] : "");

  return {
    id: u.id ?? u._id ?? null,
    email: u.email || "",
    username,
    name:
      u.name ||
      [u.first_surname, u.second_surname].filter(Boolean).join(" ") ||
      username ||
      "",
    first_surname: u.first_surname || "",
    second_surname: u.second_surname || "",
    groups: Array.isArray(u.groups)
      ? u.groups
      : u.group_id != null
      ? [u.group_id]
      : [],
    created_at: u.created_at || "",
    is_admin: bool(u.is_admin),
    disabled: bool(u.disabled),
    permissions: u.permissions || {},
  };
};

export default function Profile() {
  const { user: authUser } = useAuth();

  const [err, setErr] = useState("");
  const [groupNames, setGroupNames] = useState([]);

  const me = useMemo(
    () => (authUser ? normalizeUser(authUser) : null),
    [authUser]
  );

  useEffect(() => {
    if (!me || !Array.isArray(me.groups) || me.groups.length === 0) {
      setGroupNames([]);
      return;
    }

    let cancel = false;

    (async () => {
      try {
        const result = await Promise.all(
          me.groups.map(async (groupId) => {
            try {
              const { data } = await api.get(
                `/group/${encodeURIComponent(groupId)}`
              );

              const g = data?.data ?? data ?? null;

              return (
                g?.name ??
                g?.nombre ??
                g?.title ??
                `Grupo ${groupId}`
              );
            } catch {
              return `Grupo ${groupId}`;
            }
          })
        );

        if (!cancel) {
          setGroupNames(result.filter(Boolean));
        }
      } catch {
        if (!cancel) setGroupNames([]);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [me?.groups]);

  const info = useMemo(() => {
    const u = me || {};

    return {
      Nombre: u.name || "—",
      Usuario: u.username || "—",
      Email: u.email || "—",
      Grupos: groupNames.length
        ? groupNames.join(", ")
        : "—",
      Creado: u.created_at
        ? new Date(u.created_at).toLocaleString()
        : "—",
      Admin: u.is_admin ? "SÍ" : "NO",
      Estatus: u.disabled ? "SUSPENDIDO" : "ACTIVO",
    };
  }, [me, groupNames]);
  
  const uiPerms = useMemo(() => apiPermsToPlatform(me?.permissions || {}), [me?.permissions]);

  const handleSavePerms = async (nextUiPerms) => {
    if (!me?.is_admin || !me?.id) return;
    try {
      const apiPerms = platformToApiPerms(nextUiPerms);
      await updateUser(me.id, { permissions: apiPerms });
      alert("Permisos actualizados.");
      // Si quieres reflejarlo enseguida en esta pantalla sin esperar a refresh global:
      // me.permissions = apiPerms;  (o guarda en un estado local si prefieres)
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "No se pudieron actualizar los permisos.");
    }
  };

  return (
    <DashboardLayout>
      <Header />
      <SoftBox mt={5} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2 }}>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              <ProfileInfoCard
                title="Información de perfil"
                description=""
                info={info}
                social={[]}
                shadow={false}
                loading={!me}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <PlatformSettings
              isAdmin={!!me?.is_admin}
              permissions={uiPerms}
              onSave={handleSavePerms}   // solo hace algo si es admin
            />
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
